import { FC, PropsWithChildren, createContext, useContext } from "react";
import { Navigate, useParams, useLocation, matchPath } from "react-router-dom";
import { TStudySubject, useStudySubjectAccessCheck } from "@/infrastructure/adapters/study";
import { AppRoutes } from "@/routes/config";
import { LoadingScreen } from "@/components/elements/Loading/Loading";

/** Context value provided to children — subject is guaranteed non-null after guard passes */
type TStudyAccessGuardContext = {
  subject: TStudySubject;
};

const StudyAccessGuardContext = createContext<TStudyAccessGuardContext | null>(null);

/** Access the guarded subject data. Must be used inside StudyAccessGuardProvider. */
export const useStudyAccessGuard = (): TStudyAccessGuardContext => {
  const ctx = useContext(StudyAccessGuardContext);
  if (!ctx) throw new Error("useStudyAccessGuard must be used within StudyAccessGuardProvider");
  return ctx;
};

/**
 * Guards study sub-routes based on level assessment status.
 * Provides the resolved subject via context so children don't re-fetch.
 *
 * Route-specific redirect logic:
 *
 * - Subject route: if learner already assessed (hasLevelAssessment)
 *   -> redirect to studyContents.
 *
 * - Diagnosis route: if learner already assessed (hasLevelAssessment)
 *   -> redirect to studyContents.
 *
 * - Content routes (contents, word-practice, word-quiz, level-practice,
 *   level-quiz, text-explanation): if learner has NOT assessed
 *   -> redirect to studySubject.
 *
 * Wrap this around study sub-route elements in the route tree.
 */
export const StudyAccessGuardProvider: FC<PropsWithChildren> = ({ children }) => {
  const { subjectId } = useParams<{ subjectId: string }>();
  const { pathname } = useLocation();
  const { subject, hasLevelAssessment, isLoading } = useStudySubjectAccessCheck(subjectId);

  if (isLoading) return <LoadingScreen />;

  // Subject not found in list -> go back to study page
  if (!subjectId || !subject) {
    return <Navigate to={AppRoutes.study} replace />;
  }

  const contentsPath = AppRoutes.studyContents.replace(":subjectId", subjectId);
  const isSubjectRoute = !!matchPath(AppRoutes.studySubject, pathname);
  const isDiagnosisRoute = !!matchPath(AppRoutes.studyDiagnosis, pathname);
  const isAccessed = hasLevelAssessment || subject.hasAnswered;

  // Subject / Diagnosis route: learner already assessed -> go to contents
  if ((isSubjectRoute || isDiagnosisRoute) && isAccessed) {
    return <Navigate to={contentsPath} replace />;
  }

  // Content routes: learner has NOT assessed -> go to subject page
  if (!isSubjectRoute && !isDiagnosisRoute && !isAccessed) {
    return <Navigate to={AppRoutes.studySubject.replace(":subjectId", subjectId!)} replace />;
  }

  return <StudyAccessGuardContext.Provider value={{ subject }}>{children}</StudyAccessGuardContext.Provider>;
};
